import "./App.css";
import { useEffect } from "react";
import { Switch, Route, useLocation, Redirect, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Walkthrough from "./components/start/Walkthrough";
import Disclaimer from "./components/start/Disclaimer";
import Tunneltype from "./components/start/Tunneltype";
import Contact from "./components/start/Contact";
import Dshape from "./components/dshape/Dshape";
import Debugscreen from "./components/Debugscreen";
import Horseshoe from "./components/horseshoe/Horseshoe";
import Circular from "./components/circular/Circular";
import Shaft from "./components/shafts/Shaft";
import HelpButton from "./components/shared/HelpButton";
import TrialBadge from "./components/shared/TrialBadge";
import AccountMenu from "./components/layout/AccountMenu";
import DashboardPage from "./components/dashboard/DashboardPage";
import AdminPanel from "./components/admin/AdminPanel";
import SubscriptionPage from "./components/subscription/SubscriptionPage";
import FlowDiagramPage from "./components/flow/FlowDiagramPage";
import LoginPage from "./components/auth/LoginPage";
import VerifyEmailPage from "./components/auth/VerifyEmailPage";
import VerifyEmailPendingPage from "./components/auth/VerifyEmailPendingPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import CompleteProfilePage from "./components/auth/CompleteProfilePage";
import TermsAndConditionsPage from "./components/auth/TermsAndConditionsPage";
import PasswordGate from "./components/start/PasswordGate";
import FlowResetHandler from "./components/auth/FlowResetHandler";
import BackNavigationGuard from "./components/auth/BackNavigationGuard";
import AuthGuard from "./guards/AuthGuard";
import SubscriptionGuard from "./guards/SubscriptionGuard";
import PermissionGuard from "./guards/PermissionGuard";
import { getRequiredFlowStep } from "./helpers/flow.helpers";

const MODULE_PERMISSIONS = Object.freeze({
  DSHAPE: ["PERM_DSHAPE_PLOT", "PERM_DSHAPE_ORIENT"],
  HORSESHOE: ["PERM_HORSESHOE"],
  CIRCULAR: ["PERM_CIRCULAR"],
  SHAFT: ["PERM_SHAFT"],
  ADMIN: ["PERM_ADMIN"],
});

const RouteToastReset = () => {
  const location = useLocation();
  const { removeAllToasts, toastStack, removeToast } = useToasts();
  useEffect(() => {
    if (typeof removeAllToasts === "function") {
      removeAllToasts();
    } else if (Array.isArray(toastStack)) {
      toastStack.forEach((t) => removeToast(t.id));
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const ProtectedRoute = ({ children, permissions = [] }) => (
  <AuthGuard>
    <SubscriptionGuard>
      <PermissionGuard permissions={permissions}>{children}</PermissionGuard>
    </SubscriptionGuard>
  </AuthGuard>
);

const mapFlowProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  access: state.subscription.access,
  synced: state.subscription.synced,
  bootstrapped: state.auth.bootstrapped,
  loading: state.auth.loading,
});

const PendingScreen = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f5f5f5",
    }}
  >
    <div className="text-center">
      <div className="spinner-border text-success" role="status" />
      <p className="mt-3 text-muted mb-0">Loading...</p>
    </div>
  </div>
);

const DefaultRedirect = ({ isAuthenticated, access, synced, bootstrapped, loading }) => {
  // Wait for the session to be restored before deciding where to send anyone.
  // Redirecting to /login while still bootstrapping stranded signed-in users:
  // /login deliberately never redirects away, so a reload landed there and
  // stayed there even though the refresh succeeded a moment later.
  if (!bootstrapped || loading) {
    return <PendingScreen />;
  }
  const step = getRequiredFlowStep({ isAuthenticated, access, synced, bootstrapped });
  if (step) {
    return <Redirect to={step} />;
  }
  return <Redirect to="/" />;
};

const ConnectedDefaultRedirect = connect(mapFlowProps)(DefaultRedirect);

const HomeRoute = (props) => {
  const { isAuthenticated, access, synced, bootstrapped, loading } = props;

  if (!bootstrapped || loading) {
    return <PendingScreen />;
  }

  const step = getRequiredFlowStep({ isAuthenticated, access, synced, bootstrapped });
  if (step) {
    return <Redirect to={step} />;
  }

  return (
    <ProtectedRoute>
      <Walkthrough />
    </ProtectedRoute>
  );
};

const ConnectedHomeRoute = connect(mapFlowProps)(HomeRoute);

/**
 * The app root is owned by HomeRoute, which waits for bootstrap and then sends
 * the user to login, subscription or the app as their real state dictates.
 *
 * This used to force `#/` to /login on mount regardless of session or bootstrap
 * state. Because /login deliberately never redirects away, a signed-in user who
 * reloaded — or simply opened the app — was dumped back on the login form with
 * a perfectly valid session.
 */
const AppEntryRedirect = () => null;

function App() {
  return (
    <>
      <AppEntryRedirect />
      <FlowResetHandler />
      <BackNavigationGuard />
      <RouteToastReset />
      <HelpButton />
      <TrialBadge />
      <AccountMenu />
      <Switch>
        <Route path="/login">
          <LoginPage />
        </Route>

        <Route path="/access">
          <PasswordGate />
        </Route>

        <Route path="/complete-profile">
          <CompleteProfilePage />
        </Route>

        <Route path="/subscription">
          <SubscriptionPage />
        </Route>

        <Route path="/flow">
          <FlowDiagramPage />
        </Route>

        <Route path="/verify-email">
          <VerifyEmailPage />
        </Route>

        <Route path="/verify-email-pending">
          <VerifyEmailPendingPage />
        </Route>

        <Route path="/reset-password">
          <ResetPasswordPage />
        </Route>

        <Route path="/disclaimer">
          <Disclaimer />
        </Route>

        <Route path="/terms">
          <TermsAndConditionsPage />
        </Route>

        <Route exact path="/">
          <ConnectedHomeRoute />
        </Route>

        <Route path="/dashboard">
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin">
          <ProtectedRoute permissions={MODULE_PERMISSIONS.ADMIN}>
            <AdminPanel />
          </ProtectedRoute>
        </Route>

        <Route path="/choosetype">
          <ProtectedRoute>
            <Tunneltype />
          </ProtectedRoute>
        </Route>
        <Route path="/d_tunnel">
          <ProtectedRoute permissions={MODULE_PERMISSIONS.DSHAPE}>
            <Dshape />
          </ProtectedRoute>
        </Route>
        <Route path="/horse_shoe_tunnel">
          <ProtectedRoute permissions={MODULE_PERMISSIONS.HORSESHOE}>
            <Horseshoe />
          </ProtectedRoute>
        </Route>
        <Route path="/circular_tunnel">
          <ProtectedRoute permissions={MODULE_PERMISSIONS.CIRCULAR}>
            <Circular />
          </ProtectedRoute>
        </Route>
        <Route path="/shaft">
          <ProtectedRoute permissions={MODULE_PERMISSIONS.SHAFT}>
            <Shaft />
          </ProtectedRoute>
        </Route>
        <Route path="/contact">
          <ProtectedRoute>
            <Contact />
          </ProtectedRoute>
        </Route>
        <Route path="/debug">
          <ProtectedRoute>
            <Debugscreen />
          </ProtectedRoute>
        </Route>

        <Route path="*">
          <ConnectedDefaultRedirect />
        </Route>
      </Switch>
    </>
  );
}

export default App;
